import { z, type ZodType } from '@customerio/hydra-validation';
import type { ServicesApiDomain } from '@/services/api/services-api';
import { defineEntity } from '../shared/define-entity';

/**
 * Domain entity — sending domains for a workspace.
 *
 * Wire shape uses unix-second `created_at`/`updated_at` (with `_at`
 * suffix), unlike most other entities. We keep them as numbers for now
 * since the only consumer (`delivery-mode-banner`) doesn't use the
 * timestamps; introducing Date transforms here would be churn for no
 * benefit. Update this when a date-aware consumer lands.
 */

export type Domain = ServicesApiDomain;

/**
 * The fields the only current consumer (`delivery-mode-banner`)
 * actually reads. Asserting these at parse time means a malformed
 * response surfaces as a Zod error instead of silently producing
 * incorrect banner state. Other fields pass through via `looseObject`
 * — declare them here as consumers grow.
 *
 * `verified` is `*bool` in Go (`model.Domain.Verified`) so the wire
 * legitimately emits `null`. We accept the three-state shape directly;
 * consumers use `verified === true` to distinguish "verified" from
 * "not yet verified" / "unknown".
 */
const DomainFromApi = z.looseObject({
  id: z.string(),
  verified: z.boolean().nullable(),
}) as unknown as ZodType<Domain, unknown>;

/** @public */
export const domain = defineEntity<Domain, Domain, void, undefined, 'workspace'>({
  name: 'domain',
  scope: 'workspace',
  pk: (d) => d.id,
  schema: { read: DomainFromApi },
});

export const domainKeys = domain.keys;

export function parseDomain(raw: ServicesApiDomain): Domain {
  return domain.parse(raw);
}
